import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import EmailEditor from 'react-email-editor';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
};

const VARIABLES = [
  { name: 'fullName', label: 'Full Name' },
  { name: 'email', label: 'Email' },
  { name: 'bosName', label: 'BOS Name' },
  { name: 'courseCode', label: 'Course Code' },
  { name: 'courseName', label: 'Course Name' },
  { name: 'setterDueDate', label: 'Setter Due Date' },
  { name: 'moderatorDueDate', label: 'Moderator Due Date' },
];

// =============================================
// TEMPLATE EDITOR (Full page with Unlayer)
// =============================================
function TemplateEditor({ tenantId, template, categories, onClose, onSaved, onCategoryCreated }) {
  const isEdit = !!template;
  const emailEditorRef = useRef(null);
  const [form, setForm] = useState({
    templateName: '',
    categoryId: null,
    emailSubject: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [creatingCat, setCreatingCat] = useState(false);

  const categoryOptions = useMemo(() =>
    categories.map(c => ({ value: c.CategoryID, label: c.CategoryName })),
    [categories]
  );

  // Load template data for edit
  useEffect(() => {
    if (!isEdit) return;
    async function loadDetail() {
      try {
        const res = await api.get(`/qp-settings/email-templates/detail/${template.TemplateID}`);
        if (res.data.success) {
          const t = res.data.data;
          setForm({
            templateName: t.TemplateName || '',
            categoryId: t.CategoryID ? { value: t.CategoryID, label: t.CategoryName } : null,
            emailSubject: t.EmailSubject || '',
            description: t.Description || '',
          });
          // Load design into editor when ready
          if (editorReady && emailEditorRef.current && t.DesignJSON) {
            emailEditorRef.current.editor.loadDesign(t.DesignJSON);
          }
        }
      } catch {
        toast.error('Failed to load template');
      }
    }
    loadDetail();
  }, [isEdit, template, editorReady]);

  const onEditorReady = () => {
    setEditorReady(true);
    // Register merge tags
    if (emailEditorRef.current?.editor) {
      emailEditorRef.current.editor.registerCallback('image', function (file, done) {
        // Basic image upload placeholder - can be enhanced with blob storage
        const reader = new FileReader();
        reader.onload = () => done({ progress: 100, url: reader.result });
        reader.readAsDataURL(file.attachments[0]);
      });
    }
  };

  const handleCreateCategory = async (inputValue) => {
    setCreatingCat(true);
    try {
      const res = await api.post('/qp-settings/email-categories', { tenantId, categoryName: inputValue.trim() });
      if (res.data.success) {
        const newOpt = { value: res.data.data.categoryId, label: res.data.data.categoryName };
        setForm(f => ({ ...f, categoryId: newOpt }));
        onCategoryCreated(res.data.data);
        toast.success(`Category "${inputValue.trim()}" created`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create category');
    } finally {
      setCreatingCat(false);
    }
  };

  const handleSave = () => {
    if (!form.templateName.trim()) return toast.error('Template Name is required');
    if (!form.emailSubject.trim()) return toast.error('Email Subject is required');

    if (!emailEditorRef.current?.editor) return toast.error('Editor not ready');

    emailEditorRef.current.editor.exportHtml((data) => {
      const { design, html } = data;
      doSave(design, html);
    });
  };

  const doSave = async (designJSON, htmlContent) => {
    setSaving(true);
    try {
      const payload = {
        tenantId,
        templateName: form.templateName.trim(),
        categoryId: form.categoryId?.value || null,
        emailSubject: form.emailSubject.trim(),
        description: form.description.trim() || null,
        designJSON,
        htmlContent,
      };

      if (isEdit) {
        await api.put(`/qp-settings/email-templates/${template.TemplateID}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/qp-settings/email-templates', payload);
        toast.success('Template created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    toast.info(`{{${varName}}} copied! Paste it in the editor.`, { autoClose: 2000 });
  };

  return (
    <div className="template-editor-fullpage">
      {/* Header bar */}
      <div className="template-editor-header">
        <button className="btn btn-outline btn-sm" onClick={onClose}>← Back to Templates</button>
        <h3>{isEdit ? 'Edit Template' : 'Create Email Template'}</h3>
        <button className="btn btn-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : isEdit ? 'Update Template' : 'Save Template'}
        </button>
      </div>

      {/* Form fields */}
      <div className="template-editor-form">
        <div className="template-form-row" style={{ marginBottom: 10 }}>
          <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
            <label className="required-label">Template Name</label>
            <input
              type="text"
              placeholder="Template Name *"
              value={form.templateName}
              onChange={(e) => setForm({ ...form, templateName: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>Category</label>
            <CreatableSelect
              options={categoryOptions}
              value={form.categoryId}
              onChange={(val) => setForm({ ...form, categoryId: val })}
              onCreateOption={handleCreateCategory}
              placeholder="Search or create..."
              isClearable
              isSearchable
              isLoading={creatingCat}
              styles={selectStyles}
              formatCreateLabel={(input) => `Create "${input}"`}
              noOptionsMessage={() => 'Type to create a new category'}
            />
          </div>
        </div>
        <div className="template-form-row" style={{ marginBottom: 10 }}>
          <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
            <label className="required-label">Email Subject</label>
            <input
              type="text"
              placeholder="Email Subject *"
              value={form.emailSubject}
              onChange={(e) => setForm({ ...form, emailSubject: e.target.value })}
            />
            <span className="form-hint">You can use variables like {'{{fullName}}'}, {'{{email}}'}, etc.</span>
          </div>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>Description</label>
            <input
              type="text"
              placeholder="Optional description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>

        {/* Available Variables */}
        <div className="template-variables-bar">
          <span className="template-variables-label">ⓘ <strong>Available Variables:</strong></span>
          {VARIABLES.map((v) => (
            <button
              key={v.name}
              className="template-variable-chip"
              onClick={() => insertVariable(v.name)}
              title={`Click to copy {{${v.name}}}`}
            >
              {`{{${v.name}}}`}
            </button>
          ))}
        </div>
      </div>

      {/* Unlayer Editor */}
      <div className="template-editor-canvas">
        <EmailEditor
          ref={emailEditorRef}
          onReady={onEditorReady}
          minHeight="700px"
          style={{ height: '100%', minHeight: '700px' }}
          options={{
            displayMode: 'email',
            appearance: { theme: 'light' },
            features: { textEditor: { spellChecker: false } },
            mergeTags: VARIABLES.reduce((acc, v) => {
              acc[v.name] = { name: v.label, value: `{{${v.name}}}` };
              return acc;
            }, {}),
          }}
        />
      </div>
    </div>
  );
}

// =============================================
// PREVIEW MODAL
// =============================================
function PreviewModal({ template, onClose }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.get(`/qp-settings/email-templates/detail/${template.TemplateID}`);
        if (res.data.success) {
          setHtml(res.data.data.HtmlContent || '<p>No content</p>');
        }
      } catch {
        setHtml('<p>Failed to load preview</p>');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [template]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" style={{ maxWidth: 800, maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Preview: {template.TemplateName}</h2>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 13, color: '#616161', marginBottom: 12 }}>
          <strong>Subject:</strong> {template.EmailSubject}
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
        ) : (
          <div className="template-preview-frame">
            <iframe
              title="Email Preview"
              srcDoc={html}
              style={{ width: '100%', height: 500, border: '1px solid #e0e0e0', borderRadius: 4 }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================
// MAIN TAB - Template List
// =============================================
function EmailTemplatesTab({ tenantId }) {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const [tplRes, catRes] = await Promise.all([
        api.get(`/qp-settings/email-templates/${tenantId}`),
        api.get(`/qp-settings/email-categories/${tenantId}`),
      ]);
      if (tplRes.data.success) setTemplates(tplRes.data.data);
      if (catRes.data.success) setCategories(catRes.data.data);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (templateId) => {
    if (!(await confirmDelete('Email Template'))) return;
    try {
      await api.delete(`/qp-settings/email-templates/${templateId}`);
      toast.success('Template deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleCategoryCreated = (data) => {
    setCategories(prev => [...prev, { CategoryID: data.categoryId, CategoryName: data.categoryName, IsActive: true }]);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Full-page editor mode
  if (showEditor) {
    return (
      <TemplateEditor
        tenantId={tenantId}
        template={editTemplate}
        categories={categories}
        onClose={() => { setShowEditor(false); setEditTemplate(null); }}
        onSaved={() => { setShowEditor(false); setEditTemplate(null); loadData(); }}
        onCategoryCreated={handleCategoryCreated}
      />
    );
  }

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Email Templates {templates.length > 0 && <span style={{ fontWeight: 400, fontSize: 14, color: '#616161' }}>({templates.length})</span>}</h3>
        <button className="btn btn-add" onClick={() => { setEditTemplate(null); setShowEditor(true); }}>
          + Create Template
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No email templates created. Click "Create Template" to design one using the drag-and-drop editor.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Template Name</th>
              <th>Category</th>
              <th>Subject</th>
              <th>Created By</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.TemplateID}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>✉</span>
                    <div>
                      <strong>{t.TemplateName}</strong>
                      {t.Description && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{t.Description}</div>}
                    </div>
                  </div>
                </td>
                <td>
                  {t.CategoryName ? (
                    <span className="badge badge-provider">{t.CategoryName}</span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td style={{ color: '#616161', fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.EmailSubject}
                </td>
                <td style={{ color: '#616161', fontSize: 13 }}>{t.CreatedByName || '-'}</td>
                <td style={{ color: '#616161', fontSize: 13 }}>{formatDate(t.UpdatedAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn" title="Preview" style={{ color: '#00796b' }} onClick={() => setPreviewTemplate(t)}>👁</button>
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditTemplate(t); setShowEditor(true); }}>✏</button>
                    <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(t.TemplateID)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {previewTemplate && (
        <PreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </>
  );
}

export default EmailTemplatesTab;
